import { Button } from "@msuite/ui/button";
import { DataGrid } from "@msuite/ui/data-grid";
import { Divider } from "@msuite/ui/divider";
import { Icon } from "@msuite/ui/icons";
import { useTranslation } from "@msuite/ui/localization";
import { useQuery } from "@msuite/ui/query";
import { responseHandler } from "@msuite/ui/utils";
import { getAddressString } from "@msuite/utils/strings";
import React from "react";
import { client } from "@/hono";
import { useAuth } from "@/hooks/use-auth";
import { useCitadel } from "@/hooks/use-citadel";

/** Props Interface */
interface OrganizationPopoverProps {
	orgId: string;
}

export function OrganizationPopover({ orgId }: OrganizationPopoverProps) {
	/** Hooks */
	const { t } = useTranslation();
	const { organization } = useAuth();
	const { citadel } = useCitadel();
	// Same dead link as the crew menu offers: /partners/:orgId needs `read Partner`.
	const isPartnerOrg = organization?.id !== orgId;
	const informationIsDenied = !!citadel && citadel.cannot("read", "Partner");

	/** Query */
	const { data } = useQuery({
		queryKey: [orgId],
		queryFn: async () => {
			const response = await client.organizations[":orgId"].$get({
				param: { orgId },
			});
			const data = await responseHandler(response);
			return {
				organization: data.organization,
				partnership: data.partnership,
			};
		},
	});

	/** Functions */
	function handleOnNavigate() {
		window.open(`/partners/${orgId}`);
	}

	/** Render */
	return (
		<div className="w-[400px] card v-stack">
			<p className="font-bold">{data?.organization?.name}</p>
			<Divider variant="card" />
			<DataGrid>
				<DataGrid.Row>
					<DataGrid.Label>{t("address.address")}</DataGrid.Label>
					<DataGrid.Value>{getAddressString(data?.organization)}</DataGrid.Value>
				</DataGrid.Row>
			</DataGrid>
			{isPartnerOrg && !informationIsDenied && (
				<React.Fragment>
					<Divider variant="card" />
					<div className="flex flex-row gap-2 col-span-2">
						<Button
							variant="white"
							onClick={handleOnNavigate}
							icon={<Icon name="ExternalLink" />}
						>
							{t("general.information")}
						</Button>
					</div>
				</React.Fragment>
			)}
		</div>
	);
}
